import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Search, X } from 'lucide-react';
import { filtrujStacje, stacjeAlfabetycznie, type StacjaNaLiscie } from '@/lib/stacjeMetra';
import type { LiniaMetra } from '@/data/warsawMetroLines';
import { ChipLiniiMetra } from '@/components/offer/WarsawMetroBadge';

/**
 * Pełnoekranowy wybór stacji metra — osobny ekran nakładki obszaru.
 *
 * DLACZEGO OSOBNY EKRAN, A NIE POWRÓT CHMURY 39 CHIPÓW. Chmura stacji na wejściu zepchnęła
 * dzielnice poza ekran i została usunięta (decyzja Piotra 26.09) — stacje pokazują się w
 * kontekście dzielnicy. Kontekst dzielnicy nie odpowiada jednak na pytanie „chcę przy
 * Kabatach": człowiek zna nazwę stacji, a nie to, w jakiej dzielnicy ona leży. Ten ekran
 * odpowiada na nie wprost i NIE zmienia poziomu głównego nakładki — wchodzi się na niego
 * świadomie, jednym dotknięciem, i wraca strzałką.
 *
 * KOLOR LINII PRZED NAZWĄ, bo plan metra człowiek ma w głowie jako dwie kreski, nie jako
 * spis alfabetyczny: czerwona pastylka zawęża 38 pozycji do 21 bez czytania. Świętokrzyska
 * ma obie — i dlatego pastylka jest listą, a nie jednym znakiem.
 *
 * WYBÓR JEST WIELOKROTNY i ekran po dotknięciu ZOSTAJE. Tak samo zachowują się chipy
 * dzielnic w nakładce, z tego samego powodu: przeglądanie jest z natury wielokrotne, a
 * zamknięcie po pierwszej stacji kazałoby otwierać ekran od nowa przy drugiej. Wyjście
 * jest tam, gdzie zwykle: „Pokaż N ofert" w stopce (stopka należy do nakładki, nie do
 * tego ekranu — licznik ma jedno źródło i nie rozjeżdża się z listą).
 */
function PlakietkaLinii({ linia }: { linia: LiniaMetra }) {
  const { t } = useTranslation();
  return (
    <span aria-label={t('search.area.metro_line', { line: linia })} className="inline-flex">
      <ChipLiniiMetra linia={linia} className="h-5 w-7" />
    </span>
  );
}

export function EkranStacjiMetra({
  wybrane,
  naPrzelacz,
  naPowrot,
  pasek,
}: {
  /** Klucze wybranych obszarów (`m:Nazwa` dla stacji) — ten sam zbiór, co w nakładce. */
  wybrane: Set<string>;
  naPrzelacz: (stacja: StacjaNaLiscie, jestWybrana: boolean) => void;
  naPowrot: () => void;
  /** Pasek wybranych obszarów i komunikat o limicie — wspólne z nakładką. */
  pasek?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const poleRef = useRef<HTMLInputElement>(null);

  const stacje = useMemo(() => stacjeAlfabetycznie(), []);
  const widoczne = useMemo(() => filtrujStacje(stacje, query), [stacje, query]);

  return (
    <>
      <div className="border-b">
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={naPowrot}
            aria-label={t('search.area.back')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold">
            {t('search.area.stations_title')}
          </h2>
        </div>
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 rounded-2xl border bg-white px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={poleRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.area.stations_search')}
              // text-base, nie text-sm: poniżej 16 px iPhone powiększa całą stronę przy
              // fokusie (src/lib/mobileInputZoom.guard.test.ts).
              className="min-w-0 flex-1 appearance-none border-0 bg-transparent text-base outline-none placeholder:text-muted-foreground"
              type="search"
              name="station-search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              enterKeyHint="search"
              data-1p-ignore="true"
              data-lpignore="true"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); poleRef.current?.focus(); }}
                aria-label={t('search.area.clear_query')}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {pasek}

      <div className="min-h-0 flex-1 overflow-y-auto pb-24" data-testid="lista-stacji">
        {widoczne.length === 0 ? (
          <p className="px-4 py-4 text-sm text-muted-foreground">{t('search.area.stations_none')}</p>
        ) : (
          <ul>
            {widoczne.map((s) => {
              const jestWybrana = wybrane.has(`m:${s.name}`);
              return (
                <li key={s.name}>
                  <button
                    type="button"
                    aria-pressed={jestWybrana}
                    onClick={() => naPrzelacz(s, jestWybrana)}
                    className={`flex min-h-12 w-full items-center gap-2.5 border-b border-border/60 px-4 py-3 text-left text-base active:bg-muted ${
                      jestWybrana ? 'bg-primary/5 font-medium' : ''
                    }`}
                  >
                    <span className="flex shrink-0 gap-1">
                      {s.linie.map((l) => <PlakietkaLinii key={l} linia={l} />)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {s.name}
                      {s.dzielnica && <span className="text-muted-foreground"> · {s.dzielnica}</span>}
                    </span>
                    {jestWybrana && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

export default EkranStacjiMetra;
